import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Search, Power, Pencil, Trash2, X, Save,
  Loader2, Building2, CheckCircle, AlertCircle,
  RefreshCw, ChevronDown, KeyRound, Eye, EyeOff, Copy
} from 'lucide-react';
import { MUNICIPIOS } from '../../utils/cidades';

const STATUS_COR = {
  ativo:                'bg-green-100 text-green-700 border-green-200',
  pendente:             'bg-gray-100 text-gray-500 border-gray-200',
  aguardando_aprovacao: 'bg-blue-100 text-blue-700 border-blue-200',
  expirado:             'bg-red-100 text-red-600 border-red-200',
  bloqueado:            'bg-red-100 text-red-700 border-red-200',
};
const STATUS_LABEL = {
  ativo: 'Ativo', pendente: 'Pendente',
  aguardando_aprovacao: 'Aguardando', expirado: 'Expirado', bloqueado: 'Bloqueado',
};

// ── Modal de confirmação de exclusão ─────────────────────────
function ModalConfirmar({ empresa, onConfirmar, onCancelar, excluindo }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Trash2 size={22} className="text-red-500"/>
        </div>
        <h3 className="font-sora font-bold text-center text-acre-dark mb-2">Excluir empresa?</h3>
        <p className="text-sm text-gray-500 text-center mb-1">
          Você está prestes a excluir permanentemente:
        </p>
        <p className="text-sm font-semibold text-center text-acre-dark mb-5">
          {empresa?.razao_social}
        </p>
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 mb-5">
          ⚠️ Esta ação é <strong>irreversível</strong>. Todas as vagas, dados e histórico de pagamento desta empresa serão excluídos.
        </div>
        <div className="flex gap-3">
          <button onClick={onCancelar} className="btn-outline flex-1 py-2.5 text-sm justify-center">
            Cancelar
          </button>
          <button onClick={onConfirmar} disabled={excluindo}
            className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {excluindo ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
            Excluir
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal de edição de empresa ────────────────────────────────
function ModalEditar({ empresa, onSalvar, onFechar, salvando }) {
  const [form, setForm] = useState({
    nome:              empresa.nome || '',
    email:             empresa.email || '',
    razao_social:      empresa.razao_social || '',
    nome_fantasia:     empresa.nome_fantasia || '',
    cnpj:              empresa.cnpj || '',
    cidade:            empresa.cidade || '',
    setor:             empresa.setor || '',
    porte:             empresa.porte || '',
    telefone_comercial:empresa.telefone_comercial || '',
    status_pagamento:  empresa.status_pagamento || 'pendente',
  });

  const campo = (k) => ({ value: form[k], onChange: e => setForm(p => ({...p, [k]: e.target.value})) });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-acre-dark to-acre-green p-5 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 size={18} className="text-white"/>
            <div>
              <p className="font-sora font-bold text-white">Editar Empresa</p>
              <p className="text-white/60 text-xs">{empresa.razao_social}</p>
            </div>
          </div>
          <button onClick={onFechar} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X size={14} className="text-white"/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="label">Razão Social *</label>
              <input className="input" {...campo('razao_social')}/>
            </div>
            <div>
              <label className="label">Nome Fantasia</label>
              <input className="input" {...campo('nome_fantasia')}/>
            </div>
            <div>
              <label className="label">CNPJ</label>
              <input className="input" {...campo('cnpj')} placeholder="00.000.000/0001-00"/>
            </div>
            <div>
              <label className="label">Nome do responsável</label>
              <input className="input" {...campo('nome')}/>
            </div>
            <div>
              <label className="label">E-mail</label>
              <input className="input" type="email" {...campo('email')}/>
            </div>
            <div>
              <label className="label">Telefone</label>
              <input className="input" {...campo('telefone_comercial')} placeholder="68 3222-0000"/>
            </div>
            <div>
              <label className="label">Cidade</label>
              <select className="input" {...campo('cidade')}>
                <option value="">Selecione...</option>
                {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Setor</label>
              <input className="input" {...campo('setor')} placeholder="Ex: Comércio"/>
            </div>
            <div>
              <label className="label">Porte</label>
              <select className="input" {...campo('porte')}>
                <option value="">Selecione...</option>
                {['mei','micro','pequena','media','grande'].map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="label">Status de Pagamento</label>
              <select className="input" {...campo('status_pagamento')}>
                {['ativo','pendente','aguardando_aprovacao','expirado','bloqueado'].map(s => (
                  <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onFechar} className="btn-outline flex-1 py-2.5 text-sm justify-center">
              Cancelar
            </button>
            <button onClick={() => onSalvar(form)} disabled={salvando}
              className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {salvando ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
              Salvar alterações
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal reset de senha ──────────────────────────────────────
function ModalResetSenha({ usuario, onFechar }) {
  const [novaSenha, setNovaSenha] = useState('');
  const [verSenha,  setVerSenha]  = useState(false);
  const [salvando,  setSalvando]  = useState(false);
  const [resultado, setResultado] = useState(null);

  const gerar = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$!';
    let s = '';
    for (let i = 0; i < 10; i++) s += chars[Math.floor(Math.random() * chars.length)];
    setNovaSenha(s);
  };

  const salvar = async () => {
    if (!novaSenha || novaSenha.length < 8) return toast.error('Mínimo 8 caracteres.');
    setSalvando(true);
    try {
      const { data } = await api.post(`/admin/usuarios/${usuario.id}/resetar-senha`, { nova_senha: novaSenha });
      setResultado(data.nova_senha);
      toast.success(data.mensagem);
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao resetar.'); }
    setSalvando(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
            <KeyRound size={17} className="text-indigo-600"/>
          </div>
          <div>
            <p className="font-sora font-bold text-acre-dark">Resetar Senha</p>
            <p className="text-xs text-gray-400 truncate max-w-[200px]">{usuario.razao_social || usuario.nome}</p>
          </div>
          <button onClick={onFechar} className="ml-auto w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
            <X size={13}/>
          </button>
        </div>

        {!resultado ? (
          <div className="space-y-3">
            <div>
              <label className="label">Nova senha *</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input className="input pr-8 font-mono text-sm" type={verSenha ? 'text' : 'password'}
                    value={novaSenha} onChange={e => setNovaSenha(e.target.value)}
                    placeholder="Mín. 8 caracteres"/>
                  <button type="button" onClick={() => setVerSenha(v => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400">
                    {verSenha ? <EyeOff size={13}/> : <Eye size={13}/>}
                  </button>
                </div>
                <button onClick={gerar} className="btn-outline text-xs px-2.5 flex-shrink-0" title="Gerar senha aleatória">
                  🎲
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">A empresa deverá trocar a senha no próximo acesso.</p>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={onFechar} className="btn-outline flex-1 py-2 text-sm justify-center">Cancelar</button>
              <button onClick={salvar} disabled={salvando || novaSenha.length < 8}
                className="btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-1.5 disabled:opacity-60">
                {salvando ? <Loader2 size={13} className="animate-spin"/> : <KeyRound size={13}/>}
                Resetar
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
              <p className="text-xs text-green-600 font-semibold mb-1">✅ Senha redefinida com sucesso!</p>
              <p className="text-xs text-gray-500 mb-2">Comunique a nova senha à empresa:</p>
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-2">
                <code className="flex-1 font-mono text-sm text-acre-dark">{resultado}</code>
                <button onClick={() => { navigator.clipboard.writeText(resultado); toast.success('Copiado!'); }}
                  className="text-gray-400 hover:text-acre-green flex-shrink-0">
                  <Copy size={14}/>
                </button>
              </div>
            </div>
            <button onClick={onFechar} className="btn-primary w-full py-2.5 text-sm justify-center">Fechar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────
export default function AdminEmpresas() {
  const [empresas,  setEmpresas]  = useState([]);
  const [busca,     setBusca]     = useState('');
  const [status,    setStatus]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [editando,  setEditando]  = useState(null);
  const [excluindo, setExcluindo] = useState(null);
  const [confirmEx, setConfirmEx] = useState(null);
  const [salvando,  setSalvando]  = useState(false);
  const [toggling,  setToggling]  = useState(null);
  const [resetando, setResetando] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (busca)  q.set('busca', busca);
    if (status) q.set('status', status);
    try {
      const { data } = await api.get(`/admin/empresas?${q}`);
      setEmpresas(data);
    } catch { toast.error('Erro ao carregar empresas.'); }
    setLoading(false);
  }, [busca, status]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Toggle ativo/inativo ──────────────────────────────────
  const toggleAtivo = async (empresa) => {
    setToggling(empresa.id);
    try {
      await api.put(`/admin/usuarios/${empresa.id}/ativo`, { ativo: !empresa.ativo });
      toast.success(empresa.ativo ? `${empresa.razao_social} desativada.` : `${empresa.razao_social} ativada.`);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao alterar status.');
    }
    setToggling(null);
  };

  // ── Salvar edição ─────────────────────────────────────────
  const salvarEdicao = async (form) => {
    setSalvando(true);
    try {
      await api.put(`/admin/usuarios/${editando.id}`, form);
      toast.success('Empresa atualizada!');
      setEditando(null);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar.');
    }
    setSalvando(false);
  };

  // ── Excluir ───────────────────────────────────────────────
  const confirmarExclusao = async () => {
    setExcluindo(confirmEx.id);
    try {
      const { data } = await api.delete(`/admin/usuarios/${confirmEx.id}`);
      toast.success(data.mensagem);
      setConfirmEx(null);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao excluir.');
    }
    setExcluindo(null);
  };

  const total = empresas.length;

  return (
    <Layout title="Empresas Cadastradas" subtitle="Gerencie empresas, status e dados de acesso">

      {/* Modais */}
      {editando  && <ModalEditar empresa={editando} onSalvar={salvarEdicao} onFechar={() => setEditando(null)} salvando={salvando}/>}
      {confirmEx && <ModalConfirmar empresa={confirmEx} onConfirmar={confirmarExclusao} onCancelar={() => setConfirmEx(null)} excluindo={!!excluindo}/>}
      {resetando && <ModalResetSenha usuario={resetando} onFechar={() => setResetando(null)}/>}

      {/* Filtros */}
      <div className="flex gap-3 mb-5 flex-wrap items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input className="input pl-7 py-2 text-sm" placeholder="Buscar por nome ou e-mail..."
            value={busca} onChange={e => setBusca(e.target.value)}/>
        </div>
        <select className="input w-44 py-2 text-sm" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={carregar} className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-acre-green hover:border-acre-green transition-colors">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''}/>
        </button>
        <p className="text-xs text-gray-400 ml-auto">{total} empresa{total !== 1 ? 's' : ''}</p>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-acre-green"/>
        </div>
      ) : (
        <div className="space-y-2.5">
          {empresas.map(e => (
            <div key={e.id}
              className={`card p-4 flex items-center gap-3 transition-all ${!e.ativo ? 'opacity-50 bg-gray-50' : 'hover:shadow-sm'}`}>

              {/* Logo / avatar */}
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 border border-blue-100">
                <Building2 size={17} className="text-blue-600"/>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-sora font-semibold text-acre-dark text-sm">{e.razao_social}</span>
                  <span className={`text-xs font-semibold border px-1.5 py-0.5 rounded-full ${STATUS_COR[e.status_pagamento] || 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                    {STATUS_LABEL[e.status_pagamento] || e.status_pagamento}
                  </span>
                  {!e.ativo && <span className="text-xs bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded-full font-semibold">Inativa</span>}
                </div>
                <p className="text-xs text-gray-400 mt-0.5 truncate">
                  {e.email} {e.cnpj ? `· ${e.cnpj}` : ''} {e.cidade ? `· ${e.cidade}` : ''}
                </p>
                <p className="text-xs text-gray-400">
                  {e.vagas_ativas || 0} vagas ativas · {e.total_vagas || 0} total
                  {e.data_expiracao && ` · expira ${new Date(e.data_expiracao).toLocaleDateString('pt-BR')}`}
                </p>
              </div>

              {/* Ações */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Ativar / Desativar */}
                <button
                  onClick={() => toggleAtivo(e)}
                  disabled={toggling === e.id}
                  title={e.ativo ? 'Desativar empresa' : 'Ativar empresa'}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all border ${
                    e.ativo
                      ? 'border-red-200 text-red-400 hover:bg-red-50 hover:text-red-600'
                      : 'border-green-200 text-green-500 hover:bg-green-50'
                  }`}>
                  {toggling === e.id
                    ? <Loader2 size={13} className="animate-spin"/>
                    : <Power size={13}/>}
                </button>

                {/* Resetar senha */}
                <button onClick={() => setResetando(e)} title="Resetar senha"
                  className="w-8 h-8 rounded-lg border border-indigo-200 text-indigo-500 hover:bg-indigo-50 flex items-center justify-center transition-all">
                  <KeyRound size={13}/>
                </button>

                {/* Editar */}
                <button onClick={() => setEditando(e)} title="Editar empresa"
                  className="w-8 h-8 rounded-lg border border-blue-200 text-blue-500 hover:bg-blue-50 flex items-center justify-center transition-all">
                  <Pencil size={13}/>
                </button>

                {/* Excluir */}
                <button onClick={() => setConfirmEx(e)} title="Excluir empresa"
                  className="w-8 h-8 rounded-lg border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-all">
                  <Trash2 size={13}/>
                </button>
              </div>
            </div>
          ))}

          {!empresas.length && (
            <div className="card p-12 text-center">
              <Building2 size={32} className="text-gray-200 mx-auto mb-3"/>
              <p className="text-gray-400">Nenhuma empresa encontrada.</p>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
